import type { ArTableId, TypeId } from '../System.Artifacts.types';
import type { FormDeriveEntry } from '../System.FormDerive.types';
import { formDeriveEntrySources } from './formDeriveEntrySources';

/**
 * formDeriveEntrySourceCells
 *
 * Every table cell an entry READS, as a (tableId, typeId) pair. The single
 * spelling of that question: `detectFormDeriveCycle` builds its source nodes,
 * its registered cells and its `missingTableIds` from this, and
 * `formDeriveRowTrigger` decides from it whether a row write can change
 * anything a derive computes. A second spelling on either side diverges
 * silently — the trigger would skip a real change, which is the dangerous
 * direction.
 *
 * A row projection reads its partition key plus each column arm's source cell,
 * `rowReferenceProp` included: the group's reference CELL is a table read even
 * though the record behind it is not. `cases` contributes NOTHING — a guard
 * reads only columns the entry itself declares, validated earlier-only at save,
 * and registering that read would close a false cycle through the membership
 * loop and drop the whole table.
 *
 * A returned `typeId` may be DOTTED, for a `sourceCell` addressing a composite
 * sub-field. Both consumers already handle that: `cellNodeKey` root-collapses
 * it, so a sub-field read depends on the whole composite column — correct,
 * because a formula or projection writing any part of that cell rewrites the
 * document this read sees — and `isFormDeriveMaterialRowChange` compares the
 * address through `readFormDeriveCellValue`. Neither needs code of its own here.
 */
type FormDeriveEntrySourceCell = { tableId: ArTableId; typeId: TypeId };

function formDeriveEntrySourceCells(entry: FormDeriveEntry): FormDeriveEntrySourceCell[] {
  if ('columns' in entry) {
    const { tableId } = entry.source;
    return [
      { tableId, typeId: entry.source.typeId },
      ...entry.columns.flatMap(({ value }) => {
        if (value.kind === 'sourceCell' || value.kind === 'groupAggregate') {
          return [{ tableId, typeId: value.typeId }];
        }
        return value.kind === 'rowReferenceProp'
          ? [{ tableId, typeId: value.referenceTypeId }]
          : [];
      }),
    ];
  }
  return formDeriveEntrySources(entry).flatMap((source) =>
    source.kind === 'field' || source.kind === 'referenceProp'
      ? []
      : [{ tableId: source.tableId, typeId: source.typeId }],
  );
}

export { formDeriveEntrySourceCells };
export type { FormDeriveEntrySourceCell };
